import React, { FC, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router';
import {
  getCleanTypesMap,
  getRoomConditions
} from '@hkm/components/App/domain/selectors';
import * as actions from '@hkm/components/Attendant/shared/domain/actions';
import { getGuestServiceStatus } from '@hkm/components/Attendant/shared/utils/getGuestServiceStatus';
import { selectUserDetails } from '@hkm/components/Login/domain/selectors';
import {
  selectEffectiveValues,
  selectPropertyDateFormats
} from '@hkm/components/Menu/PropertySelector/domain/selectors';
import TaskProgressTypeBadge from '@hkm/components/shared/AttendantTaskProgressTypeBadge/AttendantTaskProgressTypeBadge';
import CleanTypeDescriptionModal from '@hkm/components/shared/CleanTypeDescriptionModal/CleanTypeDescriptionModal';
import GuestServiceField from '@hkm/components/shared/GuestService/GuestServiceField';
import NextUseTile from '@hkm/components/shared/NextUse/NextUseTile';
import OccupancyDiscrepancyButton from '@hkm/components/shared/OccupancyDiscrepancy/OccupancyDiscrepancyButton';
import ReservationsMovementsInfo from '@hkm/components/shared/ReservationMovements/ReservationsMovementsInfo';
import RoomStatusBadge from '@hkm/components/shared/RoomStatusBadge/RoomStatusBadge';
import * as maintenanceAttachmentsActions from '@hkm/components/shared/Templates/Maintenance/MaintenanceAttachmentsTile/domain/actions';
import { AttachmentMangeAccessLevel } from '@hkm/components/shared/Templates/Maintenance/MaintenanceAttachmentsTile/enum/attachmentMangeAccessLevel';
import { MaintenanceAttachmentData } from '@hkm/components/shared/Templates/Maintenance/MaintenanceAttachmentsTile/maintenanceAttachmentData';
import MaintenanceAttachmentsTile from '@hkm/components/shared/Templates/Maintenance/MaintenanceAttachmentsTile/MaintenanceAttachmentsTile';
import MaintenanceDetailsTile from '@hkm/components/shared/Templates/Maintenance/MaintenanceDetailsTile/MaintenanceDetailsTile';
import { MaintenanceUpdateFieldDisability } from '@hkm/components/shared/Templates/Maintenance/MaintenanceDetailsTile/models/MaintenanceUpdateFieldDisability';
import * as updateMaintenanceActions from '@hkm/components/shared/Templates/Maintenance/shared/domain/actions';
import GuestReservationDetailsTemplate from '@hkm/components/shared/Templates/Reservation/GuestReservationDetails/GuestReservationDetailsTemplate';
import SharedReservationDetailsTemplate from '@hkm/components/shared/Templates/Reservation/SharedReservationDetails/SharedReservationDetailsTemplate';
import APP_ROUTES from '@hkm/constants/routing.constants';
import { isGreenServiceObtainableByRoom } from '@hkm/shared/domain/greenServiceToggle/utils';
import {
  AddMaintenanceAttachmentData,
  RemoveMaintenanceAttachmentData,
  UpdateMaintenanceAttachmentData
} from '@hkm/shared/domain/maintenanceAttachment/uploadAttachement/models/maintenanceUploadAttachmentData';
import { GuestServiceStatusType } from '@hkm/shared/enum/guestServiceStatusType';
import { useGroupedReservationNotes } from '@hkm/shared/hooks/useGroupedReservationNotes';
import { GuestCount } from '@hkm/shared/interfaces/guestCount';
import { Permission } from '@hkm/shared/permissions/enum/Permission';
import { usePermission } from '@hkm/shared/permissions/hooks/usePermission';
import { useReservationMovement } from '@hkm/shared/reservations/reservationMovementHooks';
import { AttendantRoom } from '@hkm/types/attendant/models/AttendantRoom';
import { AttendantSheet } from '@hkm/types/attendant/models/AttendantSheet';
import { createTimeWithTimezone } from '@hkm/utils/dateHelper';
import { getLocalizedString } from '@hkm/utils/getLocalizedString';

import {
  AttendantTaskProgressType,
  RawGenericEntity,
  ReservationStatus,
  RoomMaintenancesAttachmentPathParam,
  RoomStatus,
  UpdateMaintenances
} from '@ac/library-api';
import {
  AcButton,
  AcButtonPattern
} from '@ac/mobile-components/dist/components/button';
import { AcButtonContent } from '@ac/mobile-components/dist/components/button-content';
import { AcFlex } from '@ac/mobile-components/dist/components/flex';
import {
  AcFormElement,
  AcFormGroup,
  AcFormRow
} from '@ac/mobile-components/dist/components/form-element';
import { AcMultiSelect } from '@ac/mobile-components/dist/components/multiselect';
import { AcSwitch } from '@ac/mobile-components/dist/components/switch';
import {
  AcTile,
  AcTileGroup
} from '@ac/mobile-components/dist/components/tile';
import {
  FlexDirection,
  Icon,
  JustifyContent
} from '@ac/mobile-components/dist/enums';
import { Childless } from '@ac/mobile-components/dist/interfaces/componentProps';
import { formatTestSelector } from '@ac/mobile-components/dist/utils';

export interface AttendantRoomDetailsBodyProps extends Childless {
  room: AttendantRoom;
  selectedSheet: AttendantSheet;
  maintenanceAttachments: MaintenanceAttachmentData[];
}

const AttendantRoomDetailsBody: FC<AttendantRoomDetailsBodyProps> = (
  props: AttendantRoomDetailsBodyProps
) => {
  const prefix = 'attendant-room-details-body';

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const canCreateMaintenance = usePermission(
    Permission.HousekeepingAttendantCreateMaintenanceForAssignedRoom
  );

  const customer = useSelector(selectUserDetails)!;
  const effectiveValues = useSelector(selectEffectiveValues)!;
  const cleanTypesDictionary = useSelector(getCleanTypesMap);

  const isGreenServiceObtainable = isGreenServiceObtainableByRoom(
    props.room,
    r => r.status!.code as ReservationStatus
  );
  const isGreenServiceEnabled = effectiveValues.greenService;
  const isOccupancyDiscrepancyEnabled = effectiveValues.occupancyDiscrepancy;
  const { serviceType } = props.room;
  const cleanType = cleanTypesDictionary.get(serviceType?.code || '');

  const [isCleanTypeModalVisible, setCleanTypeModalVisible] = useState(false);

  const { moveIn, moveOut } = useReservationMovement(
    props.room,
    r => r.status!.code as ReservationStatus
  );
  const hasReservationMovement = !!moveIn || !!moveOut;

  const selectedGuestServiceState = getGuestServiceStatus(props.room);

  const hasReservation = !!props.room.currentMainReservation;
  const hasSharedReservation = !!props.room.currentSharedReservations?.length;
  const hasMaintenance = !!props.room.currentMaintenance;

  const isRoomConditionsFunctionalityEnabled =
    effectiveValues.enableRoomConditions;
  const roomConditions = (useSelector(getRoomConditions) || [])
    .filter(({ isActive }) => isActive)
    .map((state: RawGenericEntity) => ({
      value: state.id!,
      itemLabel: `${state.code} - ${getLocalizedString(state.description)}`
    }));
  const selectedRoomConditions = (props.room.roomConditions || []).map(
    ({ id }) => id!
  );

  const onGuestServiceChangeChange = useCallback(
    (newStatus?: string, untilTime?: string) => {
      dispatch(
        actions.guestService.changeGuestServiceStatus.trigger({
          untilTime,
          roomId: props.room.id,
          currentStatus: selectedGuestServiceState,
          newStatus: newStatus as GuestServiceStatusType | undefined,
          versionId: props.room.housekeepingRoomVersion || 0
        })
      );
    },
    [props.room]
  );

  // get notes for active reservation and shared reservation then group them by their types
  const groupedReservationNotes = useGroupedReservationNotes(
    props.room.currentReservationsIds || []
  );

  const addMaintenanceButtonHandler = useCallback(() => {
    navigate(
      generatePath(APP_ROUTES.ATTENDANT_ASSIGNMENTS.ADD_MAINTENANCE, {
        roomId: props.room.id
      })
    );
  }, [props.room]);

  const toggleGreenService = useCallback(
    () =>
      dispatch(
        actions.greenService.toggleGreenService.trigger({
          roomId: props.room.id,
          hasGreenService: props.room.greenService,
          roomVersion: props.room.housekeepingRoomVersion || 0
        })
      ),
    [props.room]
  );

  const onRoomOccupancySave = useCallback((count: GuestCount) => {
    dispatch(actions.roomOccupancy.setRoomOccupancy.trigger(count));
  }, []);

  const onRoomOccupancyDelete = useCallback(() => {
    dispatch(actions.roomOccupancy.removeRoomOccupancy.trigger());
  }, []);

  const onMaintenanceUpdate = useCallback(
    (updateMaintenances: UpdateMaintenances) => {
      dispatch(
        updateMaintenanceActions.updateAttendantMaintenanceActionsSet.updateMaintenance.trigger(
          {
            data: updateMaintenances,
            roomId: props.room.unifiedRoomDetails.id,
            roomNumber: props.room.unifiedRoomDetails.roomNumber!,
            housekeepingRoomVersion: props.room.unifiedRoomDetails
              .housekeepingRoomVersion!
          }
        )
      );
    },
    [props.room]
  );

  const onMaintenanceAttachmentAdd = useCallback(
    (data: AddMaintenanceAttachmentData) =>
      dispatch(
        maintenanceAttachmentsActions.uploadAttendantAttachmentActionsSet.addAttachment.trigger(
          data
        )
      ),
    []
  );

  const onMaintenanceAttachmentRemove = useCallback(
    (data: RemoveMaintenanceAttachmentData) =>
      dispatch(
        maintenanceAttachmentsActions.uploadAttendantAttachmentActionsSet.removeAttachment.trigger(
          data
        )
      ),
    []
  );

  const onMaintenanceAttachmentUpdate = useCallback(
    (data: UpdateMaintenanceAttachmentData) =>
      dispatch(
        maintenanceAttachmentsActions.uploadAttendantAttachmentActionsSet.updateAttachment.trigger(
          data
        )
      ),
    []
  );

  const onMaintenanceAttachmentRequest = useCallback(
    (data: RoomMaintenancesAttachmentPathParam) =>
      dispatch(
        maintenanceAttachmentsActions.uploadAttendantAttachmentActionsSet.getAttachmentFile.trigger(
          data
        )
      ),
    []
  );

  const onCleanTypeClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setCleanTypeModalVisible(true);
    },
    []
  );

  const onNextUseModalClose = useCallback(
    () => setCleanTypeModalVisible(false),
    []
  );

  const onRoomConditionsChange = useCallback(
    (newSelectedConditions: string[]) => {
      const conditionsToRemove = selectedRoomConditions.filter(
        id => !newSelectedConditions.includes(id)
      );
      const conditionsToAdd = newSelectedConditions.filter(
        id => !selectedRoomConditions.includes(id)
      );

      dispatch(
        actions.roomConditions.changeRoomConditions.trigger({
          roomId: props.room.id,
          roomConditions: {
            add: conditionsToAdd,
            remove: conditionsToRemove
          },
          housekeepingRoomVersion: props.room.housekeepingRoomVersion || 0
        })
      );
    },
    [props.room.housekeepingRoomVersion, props.room.id]
  );

  const disableFields: MaintenanceUpdateFieldDisability = {
    returnStatusCode: true,
    toTime: true
  };

  const desiredTime =
    props.room.queueRoomEntries && props.room.queueRoomEntries[0]?.desiredTime
      ? props.room.queueRoomEntries[0]?.desiredTime
      : undefined;
  const formats = useSelector(selectPropertyDateFormats);
  const formattedDesiredTime =
    desiredTime && createTimeWithTimezone(desiredTime).format(formats.time);

  const isDesiredTimeEnabled = effectiveValues.enabledReadyBy;

  return (
    <AcTileGroup className={prefix} test-selector={prefix}>
      <AcTile
        icon={Icon.Room}
        title={t('ATTENDANT_ASSIGNMENTS.ROOM_DETAILS.ROOM_INFORMATION')}
      >
        <AcFormGroup>
          {hasReservationMovement && <ReservationsMovementsInfo small={true} />}
          <AcFlex>
            <AcFormElement
              label={t('GLOBAL.TASK_PROGRESS_TYPE.TITLE.LONG')}
              testSelector={formatTestSelector(prefix, 'cleaningStatus')}
            >
              <TaskProgressTypeBadge
                status={
                  props.room.activeTask!.progress
                    ?.code as AttendantTaskProgressType
                }
              />
            </AcFormElement>

            {isDesiredTimeEnabled && desiredTime && (
              <AcFormElement
                className="ready-by-time-room-details"
                label={t('ATTENDANT_ASSIGNMENTS.DESIRED_TIME')}
              >
                {formattedDesiredTime}
              </AcFormElement>
            )}
          </AcFlex>
          <AcFormElement
            label={t('GLOBAL.ROOM_TYPE.LONG')}
            testSelector={formatTestSelector(prefix, 'roomType')}
          >
            {t('ATTENDANT_ASSIGNMENTS.ROOM_DETAILS.ROOM_TYPE_FORMAT', {
              code: props.room.roomType!.code,
              name: props.room.roomType!.description
            })}
          </AcFormElement>

          <AcFormElement
            label={t('GLOBAL.FRONT_DESK_STATUS.LONG')}
            testSelector={formatTestSelector(prefix, 'frontdesk')}
          >
            {props.room.frontdeskStatus!.description}
          </AcFormElement>

          {isOccupancyDiscrepancyEnabled && (
            <OccupancyDiscrepancyButton
              testSelector={prefix}
              occupancy={props.room.roomOccupancy}
              onSave={onRoomOccupancySave}
              onDelete={onRoomOccupancyDelete}
            />
          )}
        </AcFormGroup>
      </AcTile>

      <AcTile
        title={t('ATTENDANT_ASSIGNMENTS.ROOM_DETAILS.HOUSEKEEPING')}
        icon={Icon.Housekeeping}
      >
        <AcFormGroup>
          <AcFormElement
            label={t('GLOBAL.ROOM_STATUS.LONG')}
            testSelector={formatTestSelector(prefix, 'roomStatus')}
          >
            <RoomStatusBadge
              status={props.room.roomStatus!.code! as RoomStatus}
              showLabel={true}
            />
          </AcFormElement>

          <AcFormRow>
            <AcFormElement
              label={t('GLOBAL.CLEARING_TYPE.TITLE')}
              testSelector={formatTestSelector(prefix, 'cleaningType')}
            >
              {cleanType?.longDescription ? (
                <>
                  <a href="#" className="link" onClick={onCleanTypeClick}>
                    {props.room.serviceType
                      ? props.room.serviceType.description
                      : t(`GLOBAL.NONE`)}
                  </a>
                  <CleanTypeDescriptionModal
                    onClose={onNextUseModalClose}
                    isVisible={isCleanTypeModalVisible}
                    cleanType={cleanType}
                  />
                </>
              ) : props.room.serviceType ? (
                props.room.serviceType.description
              ) : (
                t(`GLOBAL.NONE`)
              )}
            </AcFormElement>
            <AcFormElement
              label={t('GLOBAL.LINEN_CHANGE')}
              testSelector={formatTestSelector(prefix, 'linenChange')}
            >
              {props.room.linenChange ? t('GLOBAL.YES') : t('GLOBAL.NO')}
            </AcFormElement>
          </AcFormRow>

          <GuestServiceField
            disabled={!props.room.currentMainReservation}
            testSelector={formatTestSelector(prefix, 'guest-service')}
            onChange={onGuestServiceChangeChange}
            selectedGuestService={selectedGuestServiceState}
            serviceDeferredUntil={props.room.serviceDeferredUntil}
          />

          {isRoomConditionsFunctionalityEnabled ? (
            <AcMultiSelect
              placeholder={t('GLOBAL.SELECT')}
              label={t('ROOM_DETAILS.ROOM_CONDITIONS')}
              itemsList={roomConditions}
              selectedItem={selectedRoomConditions}
              testSelector={`${prefix}-room-conditions`}
              showInputs
              showSearch
              onChange={onRoomConditionsChange}
            />
          ) : (
            <></>
          )}

          {isGreenServiceEnabled && isGreenServiceObtainable && (
            <AcFormElement
              label={t('GLOBAL.GREEN_SERVICE.TITLE')}
              testSelector={formatTestSelector(prefix, 'greenService')}
              flatAndJustify={JustifyContent.spaceBetween}
            >
              <AcSwitch
                onChange={toggleGreenService}
                state={props.room.greenService}
                selected={props.room.greenService}
                testSelector={`${prefix}-greenServiceSwitch`}
                contractedTouchArea={true}
              />
            </AcFormElement>
          )}

          <AcFormElement
            label={t('ATTENDANT_ASSIGNMENTS.ROOM_DETAILS.EFFORT_POINTS')}
            testSelector={formatTestSelector(prefix, 'effortPoints')}
          >
            {props.room.effortPoints}
          </AcFormElement>
        </AcFormGroup>
      </AcTile>

      {hasReservation && !hasSharedReservation && (
        <GuestReservationDetailsTemplate
          roomId={props.room.id}
          reservation={props.room.currentMainReservation!}
          className="ac-spacing-top-sm"
          groupedReservationNotes={groupedReservationNotes}
          showGuestNames={props.selectedSheet.showGuestNames}
        />
      )}

      {hasSharedReservation && (
        <SharedReservationDetailsTemplate
          roomId={props.room.id}
          className="ac-spacing-top-md"
          reservations={props.room.currentReservations!}
          groupedReservationNotes={groupedReservationNotes}
          showGuestNames={props.selectedSheet.showGuestNames}
        />
      )}

      {hasMaintenance && (
        <>
          <MaintenanceDetailsTile
            disableFields={disableFields}
            onSubmit={onMaintenanceUpdate}
            unifiedRoomDetails={props.room.unifiedRoomDetails}
            maintenanceDetails={props.room.currentMaintenance!}
          />
          <MaintenanceAttachmentsTile
            attachments={props.maintenanceAttachments}
            roomId={props.room.id}
            testSelector="maintenanceAttachmentsTile"
            roomVersionId={props.room.housekeepingRoomVersion!}
            maintenanceId={props.room.currentMaintenance!.id}
            onAdd={onMaintenanceAttachmentAdd}
            onUpdate={onMaintenanceAttachmentUpdate}
            onFileRequest={onMaintenanceAttachmentRequest}
            onRemove={onMaintenanceAttachmentRemove}
            customerId={customer.id}
            manageAccessLevel={AttachmentMangeAccessLevel.Owner}
          />
        </>
      )}

      {props.selectedSheet.hasInstructions && (
        <AcTile
          title={t('ATTENDANT_ASSIGNMENTS.ROOM_DETAILS.INSTRUCTION')}
          icon={Icon.Audit}
          className="ac-spacing-top-sm"
        >
          {props.selectedSheet.instructions}
        </AcTile>
      )}

      <NextUseTile
        groupedReservedKinds={props.room}
        unifiedRoomDetails={props.room.unifiedRoomDetails}
        showGuestNames={props.selectedSheet.showGuestNames}
      />

      {canCreateMaintenance && (
        <AcFlex
          justifyContent={JustifyContent.center}
          direction={FlexDirection.row}
        >
          <AcButton
            onClick={addMaintenanceButtonHandler}
            pattern={AcButtonPattern.Tertiary}
            testSelector={`${prefix}-add-maintenance`}
            fullwidth={true}
          >
            <AcButtonContent
              icon={Icon.Plus}
              text={t(`ATTENDANT_ASSIGNMENTS.ROOM_DETAILS.ADD_MAINTENANCE`)}
            />
          </AcButton>
        </AcFlex>
      )}
    </AcTileGroup>
  );
};

export default memo(AttendantRoomDetailsBody);



// WEBPACK FOOTER //
// ./src/components/Attendant/RoomDetails/body/AttendantRoomDetailsBody.tsx